import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const relatedArticles = id ? getRelatedArticles(id, 3) : [];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-file-unknow-line text-2xl text-secondary-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-secondary-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const paragraphs = article.content.split('\n\n').filter((p) => p.trim());

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          image: article.image,
          datePublished: article.date,
          author: { '@type': 'Person', name: article.author },
          url: `https://chengquan.tv/article/${article.id}`,
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${article.title} - 成全影院`,
          description: article.summary,
          url: `https://chengquan.tv/article/${article.id}`,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '影视资讯', href: '/' },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-10">
          <div className="max-w-[800px] mx-auto">
            <div className="mb-6">
              <span className="inline-block text-xs px-3 py-1 bg-secondary-100 text-secondary-600 rounded-full mb-3 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-3 text-sm text-secondary-400">
                <span className="whitespace-nowrap">
                  <i className="ri-user-line mr-1"></i>{article.author}
                </span>
                <span className="whitespace-nowrap">
                  <i className="ri-calendar-line mr-1"></i>{article.date}
                </span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-8 bg-secondary-100">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="prose-custom">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-sm md:text-base text-foreground-800 leading-relaxed mb-4">
                  {paragraph.trim()}
                </p>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex items-center gap-2 text-sm text-secondary-500 mb-3">
                <i className="ri-share-forward-line"></i>
                <span>分享文章</span>
              </div>
              <div className="flex items-center gap-2">
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-full hover:bg-primary-100 hover:text-primary-500 transition-colors cursor-pointer">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h3 className="text-lg font-bold text-foreground-950 font-heading mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group block bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      <div className="aspect-[16/9] overflow-hidden bg-secondary-100">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-xs font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2">
                          {related.title}
                        </h4>
                        <span className="text-xs text-secondary-400 mt-1 block whitespace-nowrap">{related.date}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}